/**
 * GA4 event tracking utilities for EU pages and international funnel
 */

import * as React from 'react';

interface GA4EventData {
  event_name: string;
  service?: string;
  city?: string;
  country?: string;
  language?: string;
  currency?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

/**
 * Enhanced GA4 tracking for EU vs Swiss segmentation
 */
export class EUAnalytics {
  
  /**
   * Track EU city page views
   */
  static trackEUCityPageView(cityKey: string, service: string, language: string) {
    const cityData = this.getCityMetadata(cityKey);
    
    const eventData: GA4EventData = {
      event_name: 'eu_city_page_view',
      service,
      city: cityKey,
      country: cityData.country,
      language,
      custom_parameters: {
        is_eu_page: true,
        region: cityData.region,
        population: cityData.population,
        page_category: 'eu_service_page'
      }
    };
    
    this.sendGA4Event(eventData);
  }
  
  /**
   * Track EU lead form submissions
   */
  static trackEULeadSubmit(cityKey: string, service: string, language: string, formData: any) {
    const cityData = this.getCityMetadata(cityKey);
    
    const eventData: GA4EventData = {
      event_name: 'eu_lead_form_submit',
      service,
      city: cityKey,
      country: cityData.country,
      language,
      currency: 'EUR',
      value: this.getLeadValue(service),
      custom_parameters: {
        is_eu_lead: true,
        lead_source: 'organic_eu',
        form_type: formData.formType || 'contact',
        budget_range: formData.budget,
        timeline: formData.timeline,
        company_size: formData.companySize
      }
    };
    
    this.sendGA4Event(eventData);
  }
  
  /**
   * Track calculator usage with EU/Swiss segmentation
   */
  static trackCalculatorUsage(calculatorType: string, cityKey?: string, results?: any) {
    const isEU = cityKey ? this.isEUCity(cityKey) : false;
    const cityData = cityKey ? this.getCityMetadata(cityKey) : null;
    
    const eventData: GA4EventData = {
      event_name: 'calculator_complete',
      service: calculatorType,
      city: cityKey,
      country: cityData?.country,
      currency: isEU ? 'EUR' : 'CHF',
      value: results?.estimatedCost || 0,
      custom_parameters: {
        is_eu_page: isEU,
        calculator_type: calculatorType,
        estimated_cost: results?.estimatedCost,
        project_complexity: results?.complexity,
        lead_score: this.calculateLeadScore(results)
      }
    };
    
    this.sendGA4Event(eventData);
  }
  
  /**
   * Track cross-border funnel events
   */
  static trackCrossBorderFunnel(action: string, fromLocation: string, toLocation: string) {
    const eventData: GA4EventData = {
      event_name: 'cross_border_funnel',
      custom_parameters: {
        funnel_action: action, // 'swiss_to_eu', 'eu_to_swiss', 'eu_to_eu'
        from_location: fromLocation,
        to_location: toLocation,
        expansion_intent: true
      }
    };
    
    this.sendGA4Event(eventData);
  }
  
  /**
   * Track navigation events for international menu
   */
  static trackInternationalNavigation(clickedCity: string, fromPage: string) {
    const cityData = this.getCityMetadata(clickedCity);
    
    const eventData: GA4EventData = {
      event_name: 'international_navigation_click',
      city: clickedCity,
      country: cityData.country,
      custom_parameters: {
        click_location: 'international_menu',
        from_page: fromPage,
        destination_type: 'eu_city_page'
      }
    };
    
    this.sendGA4Event(eventData);
  }
  
  /**
   * Track sticky CTA interactions with currency toggle
   */
  static trackStickyCTAInteraction(action: string, currency: string, cityKey?: string) {
    const eventData: GA4EventData = {
      event_name: 'sticky_cta_interaction',
      city: cityKey,
      currency,
      custom_parameters: {
        cta_action: action, // 'currency_toggle', 'cta_click', 'form_open'
        selected_currency: currency,
        is_eu_page: cityKey ? this.isEUCity(cityKey) : false
      }
    };
    
    this.sendGA4Event(eventData);
  }
  
  /**
   * Send event to GA4
   */
  static sendGA4Event(eventData: GA4EventData) {
    // Check if gtag is available
    if (typeof window !== 'undefined' && typeof gtag !== 'undefined') {
      gtag('event', eventData.event_name, {
        service: eventData.service,
        city: eventData.city,
        country: eventData.country,
        language: eventData.language,
        currency: eventData.currency,
        value: eventData.value,
        ...eventData.custom_parameters
      });
    }
    
    // Also log for debugging in development
    if (import.meta.env.DEV) {
      console.log('GA4 Event:', eventData);
    }
  }
  
  /**
   * Get city metadata for tracking
   */
  private static getCityMetadata(cityKey: string) {
    const cityMap: Record<string, { country: string; region: string; population: number }> = {
      'stuttgart': { country: 'Germany', region: 'Baden-Württemberg', population: 630000 },
      'munich': { country: 'Germany', region: 'Bayern', population: 1500000 },
      'lyon': { country: 'France', region: 'Auvergne-Rhône-Alpes', population: 515000 },
      'paris': { country: 'France', region: 'Île-de-France', population: 2165000 },
      'milan': { country: 'Italy', region: 'Lombardia', population: 1366000 },
      'como': { country: 'Italy', region: 'Lombardia', population: 85000 },
      // Swiss cities
      'zurich': { country: 'Switzerland', region: 'Zürich', population: 434000 },
      'geneva': { country: 'Switzerland', region: 'Genève', population: 203000 },
      'basel': { country: 'Switzerland', region: 'Basel', population: 177000 }
    };
    
    return cityMap[cityKey] || { country: 'Unknown', region: 'Unknown', population: 0 };
  }
  
  /**
   * Check if city is in EU
   */
  private static isEUCity(cityKey: string): boolean {
    const euCities = ['stuttgart', 'munich', 'lyon', 'paris', 'milan', 'como'];
    return euCities.includes(cityKey);
  }
  
  /**
   * Calculate lead value based on service
   */
  private static getLeadValue(service: string): number {
    const leadValues: Record<string, number> = {
      'ai-video': 5000,
      'seo': 3000,
      'webdesign': 8000,
      'ecommerce': 12000,
      'consultation': 1000
    };
    
    return leadValues[service] || 2000;
  }
  
  /**
   * Calculate lead score based on form data
   */
  private static calculateLeadScore(data: any): number {
    let score = 0;
    
    // Budget scoring
    if (data?.budget) {
      if (data.budget.includes('50000+')) score += 100;
      else if (data.budget.includes('20000-50000')) score += 75;
      else if (data.budget.includes('10000-20000')) score += 50;
      else score += 25;
    }
    
    // Timeline scoring
    if (data?.timeline) {
      if (data.timeline.includes('immediate')) score += 50;
      else if (data.timeline.includes('1-3 months')) score += 35;
      else score += 20;
    }
    
    // Company size scoring
    if (data?.companySize) {
      if (data.companySize.includes('enterprise')) score += 40;
      else if (data.companySize.includes('medium')) score += 25;
      else score += 15;
    }
    
    return Math.min(score, 200); // Cap at 200
  }
}

/**
 * Hook for tracking EU page views
 */
export const useEUPageTracking = (cityKey: string, service: string, language: string) => {
  React.useEffect(() => {
    // Track page view
    EUAnalytics.trackEUCityPageView(cityKey, service, language);
    
    // Track time on page
    const startTime = Date.now();
    
    return () => {
      const timeOnPage = Date.now() - startTime;
      if (timeOnPage > 30000) { // More than 30 seconds = engaged
        EUAnalytics.sendGA4Event({
          event_name: 'engaged_session',
          service,
          city: cityKey,
          custom_parameters: {
            time_on_page: timeOnPage,
            is_eu_page: true
          }
        });
      }
    };
  }, [cityKey, service, language]);
};

// Make EUAnalytics available globally for onclick handlers
if (typeof window !== 'undefined') {
  (window as any).EUAnalytics = EUAnalytics;
}